import {
  CognitoIdentityProviderClient,
  ConfirmForgotPasswordCommand,
  ConfirmSignUpCommand,
  ForgotPasswordCommand,
  InitiateAuthCommand,
  ResendConfirmationCodeCommand,
  RespondToAuthChallengeCommand,
  RevokeTokenCommand,
  SignUpCommand
} from '@aws-sdk/client-cognito-identity-provider';
import { PublicClientApplication } from '@azure/msal-browser';
import routing from './routing.cjs';

const { ADMIN_PATH, LOGIN_PATH, safeApplicationUrl } = routing;

const LOCAL_SESSION_KEY = 'wb-parts-cognito.local-session';
const MICROSOFT_RETURN_KEY = 'wb-parts.microsoft-return';
const ADMIN_LOGIN_KEY = 'wb-parts.admin-login';
const ADMIN_SESSION_KEY = 'wb-parts.admin-session';
const ADMIN_LOGIN_HINT_KEY = 'wb-parts.admin-login-hint';
const ADMIN_AUTO_RESTORE_DISABLED_KEY = 'wb-parts.admin-auto-restore-disabled';
const LAST_SIGN_IN_METHOD_KEY = 'wb-parts.auth-last-sign-in-method';

export async function createAuthClient() {
  const response = await fetch('/api/auth-config', {
    cache: 'no-store',
    headers: { Accept: 'application/json' }
  });
  const config = await response.json().catch(() => ({}));

  if (!response.ok || !config.ok) {
    throw new Error(config.message || 'Cognito authentication is not configured.');
  }

  const client = new CognitoAuthClient(config);
  await client.initialize();
  return client;
}

class CognitoAuthClient {
  constructor(config) {
    this.config = config;
    this.localSession = readLocalSession();
    this.cognito = new CognitoIdentityProviderClient({ region: config.region });
    this.microsoft = config.microsoft ? new PublicClientApplication({
      auth: {
        clientId: config.microsoft.clientId,
        authority: config.microsoft.authority,
        redirectUri: new URL(config.callbackPath, window.location.origin).toString(),
        navigateToLoginRequestUrl: false
      },
      cache: {
        cacheLocation: 'sessionStorage'
      }
    }) : null;
  }

  async initialize() {
    if (this.microsoft) {
      await this.microsoft.initialize();
    }
  }

  async completeSignInIfPresent() {
    if (!this.microsoft) {
      return '';
    }

    const result = await this.microsoft.handleRedirectPromise();
    if (!result) {
      return '';
    }

    if (!result.idToken) {
      throw new Error('Microsoft did not return a usable identity token.');
    }

    const administratorLogin = window.sessionStorage.getItem(ADMIN_LOGIN_KEY) === 'true';
    window.sessionStorage.removeItem(ADMIN_LOGIN_KEY);
    let identityResult = result;
    let refreshFailure = '';
    if (administratorLogin && result.account) {
      try {
        const renewed = await this.microsoft.acquireTokenSilent({
          scopes: this.config.microsoft.scopes,
          account: result.account,
          forceRefresh: true
        });
        if (renewed.idToken) identityResult = renewed;
      } catch (error) {
        refreshFailure = String(error && (error.errorCode || error.code || error.name) || 'unknown-error').slice(0, 80);
      }
    }
    if (administratorLogin) {
      try {
        await this.exchangeAdministratorToken(identityResult.idToken, null, refreshFailure);
      } catch (error) {
        window.sessionStorage.setItem('wb-parts.admin-error', error.message);
      }
      return ADMIN_PATH;
    }

    const response = await fetch(this.config.microsoft.sessionEndpoint, {
      method: 'POST',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Microsoft-ID-Token': `Bearer ${identityResult.idToken}`
      }
    });
    const session = await response.json().catch(() => ({}));
    if (!response.ok || !session.ok) {
      throw new Error(session.message || 'Microsoft sign-in could not be completed.');
    }

    this.storeMicrosoftSession(session);
    const returnUrl = window.sessionStorage.getItem(MICROSOFT_RETURN_KEY) || '';
    window.sessionStorage.removeItem(MICROSOFT_RETURN_KEY);
    return safeApplicationUrl(returnUrl, window.location.origin) || LOGIN_PATH;
  }

  async refreshSession() {
    return this.refreshLocalSession();
  }

  async refreshLocalSession() {
    const session = this.localSession || readLocalSession();
    if (!session || !session.accessToken) {
      return false;
    }

    if (session.expiresAt > Math.floor(Date.now() / 1000) + 90) {
      this.localSession = session;
      return true;
    }

    if (session.provider === 'Microsoft') {
      await this.clearLocalSession();
      return false;
    }

    if (!session.refreshToken) {
      await this.clearLocalSession();
      return false;
    }

    try {
      const response = await this.cognito.send(new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: this.config.clientId,
        AuthParameters: {
          REFRESH_TOKEN: session.refreshToken
        }
      }));
      this.storeAuthenticationResult(response.AuthenticationResult, session.refreshToken);
      return true;
    } catch {
      await this.clearLocalSession();
      return false;
    }
  }

  isSignedIn() {
    return Boolean(this.localSession && this.localSession.accessToken);
  }

  getIdentity() {
    if (this.localSession) {
      return {
        email: this.localSession.email || 'Signed-in user',
        identityProvider: this.localSession.provider || 'cognito',
        userId: this.localSession.userId
      };
    }
    return null;
  }

  async getAccessToken() {
    const hasSession = await this.refreshSession();
    if (!hasSession) {
      return '';
    }
    return this.localSession ? this.localSession.accessToken : '';
  }

  async signInWithPassword(email, password) {
    const username = normalizeEmail(email);
    const initial = await this.cognito.send(new InitiateAuthCommand({
      AuthFlow: 'USER_AUTH',
      ClientId: this.config.clientId,
      AuthParameters: {
        USERNAME: username,
        PREFERRED_CHALLENGE: 'PASSWORD',
        PASSWORD: password
      }
    }));

    if (initial.AuthenticationResult) {
      return this.finishAuthentication(initial);
    }

    const responseUsername = getChallengeUsername(initial, username);

    if (!['PASSWORD', 'SELECT_CHALLENGE'].includes(initial.ChallengeName)) {
      return this.finishAuthentication(initial, responseUsername);
    }

    const challengeResponses = initial.ChallengeName === 'SELECT_CHALLENGE'
      ? { USERNAME: responseUsername, ANSWER: 'PASSWORD', PASSWORD: password }
      : { USERNAME: responseUsername, PASSWORD: password };

    const response = await this.cognito.send(new RespondToAuthChallengeCommand({
      ChallengeName: initial.ChallengeName,
      ClientId: this.config.clientId,
      ChallengeResponses: challengeResponses,
      Session: initial.Session
    }));
    return this.finishAuthentication(response, getChallengeUsername(response, responseUsername));
  }

  async startEmailOtp(email) {
    const username = normalizeEmail(email);
    let response = await this.cognito.send(new InitiateAuthCommand({
      AuthFlow: 'USER_AUTH',
      ClientId: this.config.clientId,
      AuthParameters: {
        USERNAME: username,
        PREFERRED_CHALLENGE: 'EMAIL_OTP'
      }
    }));

    if (response.AuthenticationResult) {
      return this.finishAuthentication(response);
    }

    let responseUsername = getChallengeUsername(response, username);

    if (response.ChallengeName === 'SELECT_CHALLENGE') {
      response = await this.cognito.send(new RespondToAuthChallengeCommand({
        ChallengeName: 'SELECT_CHALLENGE',
        ClientId: this.config.clientId,
        ChallengeResponses: {
          USERNAME: responseUsername,
          ANSWER: 'EMAIL_OTP'
        },
        Session: response.Session
      }));
      responseUsername = getChallengeUsername(response, responseUsername);
    }

    if (response.ChallengeName !== 'EMAIL_OTP' || !response.Session) {
      return this.finishAuthentication(response, responseUsername);
    }

    return {
      signedIn: false,
      next: 'EMAIL_OTP',
      username: responseUsername,
      session: response.Session
    };
  }

  async confirmEmailOtp(username, session, code) {
    const response = await this.cognito.send(new RespondToAuthChallengeCommand({
      ChallengeName: 'EMAIL_OTP',
      ClientId: this.config.clientId,
      ChallengeResponses: {
        USERNAME: normalizeEmail(username),
        EMAIL_OTP_CODE: String(code || '').trim()
      },
      Session: session
    }));
    return this.finishAuthentication(response, username);
  }

  async completeNewPassword(username, session, newPassword) {
    const response = await this.cognito.send(new RespondToAuthChallengeCommand({
      ChallengeName: 'NEW_PASSWORD_REQUIRED',
      ClientId: this.config.clientId,
      ChallengeResponses: {
        USERNAME: normalizeEmail(username),
        NEW_PASSWORD: newPassword
      },
      Session: session
    }));
    return this.finishAuthentication(response, username);
  }

  async signUp(email, password) {
    const username = normalizeEmail(email);
    const response = await this.cognito.send(new SignUpCommand({
      ClientId: this.config.clientId,
      Username: username,
      Password: password,
      UserAttributes: [{ Name: 'email', Value: username }]
    }));

    return {
      confirmed: Boolean(response.UserConfirmed),
      destination: response.CodeDeliveryDetails && response.CodeDeliveryDetails.Destination,
      username
    };
  }

  async confirmSignUp(email, code) {
    await this.cognito.send(new ConfirmSignUpCommand({
      ClientId: this.config.clientId,
      Username: normalizeEmail(email),
      ConfirmationCode: String(code || '').trim()
    }));
  }

  async resendSignUpCode(email) {
    return this.cognito.send(new ResendConfirmationCodeCommand({
      ClientId: this.config.clientId,
      Username: normalizeEmail(email)
    }));
  }

  async startPasswordReset(email) {
    const username = normalizeEmail(email);
    const response = await this.cognito.send(new ForgotPasswordCommand({
      ClientId: this.config.clientId,
      Username: username
    }));
    return {
      username,
      destination: response.CodeDeliveryDetails && response.CodeDeliveryDetails.Destination
    };
  }

  async confirmPasswordReset(email, code, password) {
    await this.cognito.send(new ConfirmForgotPasswordCommand({
      ClientId: this.config.clientId,
      Username: normalizeEmail(email),
      ConfirmationCode: String(code || '').trim(),
      Password: password
    }));
  }

  async signInWithMicrosoft() {
    window.sessionStorage.removeItem(ADMIN_LOGIN_KEY);
    if (!this.microsoft || !this.config.microsoft) {
      throw new Error('Microsoft sign-in is not configured.');
    }

    await this.clearLocalSession();
    window.sessionStorage.setItem(
      MICROSOFT_RETURN_KEY,
      safeApplicationUrl(window.location.href, window.location.origin) || LOGIN_PATH
    );
    await this.microsoft.loginRedirect({
      scopes: this.config.microsoft.scopes,
      prompt: 'select_account'
    });
  }

  hasMicrosoftSignIn() {
    return Boolean(this.microsoft && this.config.microsoft);
  }

  async signInAsAdministrator() {
    if (!this.microsoft) throw new Error('Microsoft sign-in is not configured.');
    window.sessionStorage.removeItem(ADMIN_AUTO_RESTORE_DISABLED_KEY);
    const restored = await this.restoreAdministratorSession();
    if (restored) {
      window.location.replace(ADMIN_PATH);
      return;
    }
    window.sessionStorage.setItem(ADMIN_LOGIN_KEY, 'true');
    const loginHint = readAdminLoginHint();
    await this.microsoft.loginRedirect({
      scopes: this.config.microsoft.scopes,
      ...(loginHint ? { loginHint } : { prompt: 'select_account' })
    });
  }

  async restoreAdministratorSession() {
    if (!this.microsoft || window.sessionStorage.getItem(ADMIN_AUTO_RESTORE_DISABLED_KEY) === 'true') return null;
    try {
      const loginHint = readAdminLoginHint();
      const accounts = this.microsoft.getAllAccounts();
      const account = accounts.find(candidate => normalizeEmail(candidate.username) === normalizeEmail(loginHint)) || (accounts.length === 1 ? accounts[0] : null);
      const result = account
        ? await this.microsoft.acquireTokenSilent({ scopes: this.config.microsoft.scopes, account, forceRefresh: true })
        : loginHint
          ? await this.microsoft.ssoSilent({ scopes: this.config.microsoft.scopes, loginHint })
          : null;
      return result?.idToken ? await this.exchangeAdministratorToken(result.idToken) : null;
    } catch {
      return null;
    }
  }

  async refreshAdministratorSession() {
    const current = readAdministratorSession();
    if (!current || current.workSessionExpiresAt * 1000 <= Date.now()) {
      this.endAdministratorSession();
      return null;
    }
    try {
      const accounts = this.microsoft?.getAllAccounts() || [];
      const account = accounts.find(candidate => normalizeEmail(candidate.username) === normalizeEmail(current.user.email));
      if (!account) throw new Error('Microsoft account is no longer available.');
      const result = await this.microsoft.acquireTokenSilent({ scopes: this.config.microsoft.scopes, account, forceRefresh: true });
      if (!result.idToken) throw new Error('Microsoft did not return a usable identity token.');
      return await this.exchangeAdministratorToken(result.idToken, current);
    } catch {
      this.endAdministratorSession();
      return null;
    }
  }

  async exchangeAdministratorToken(idToken, current = null, refreshFailure = '') {
    const response = await fetch('/api/access-admin-session', {
      method: 'POST',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(current?.sessionToken ? { 'X-WB-Admin-Token': `Bearer ${current.sessionToken}` } : {})
      },
      body: JSON.stringify({ idToken })
    });
    const session = await response.json().catch(() => ({}));
    if (!response.ok || !session.ok) throw new Error(administratorSignInError(session, refreshFailure));
    window.sessionStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(session));
    window.sessionStorage.removeItem(ADMIN_AUTO_RESTORE_DISABLED_KEY);
    if (session.user?.email) window.localStorage.setItem(ADMIN_LOGIN_HINT_KEY, normalizeEmail(session.user.email));
    return session;
  }

  endAdministratorSession() {
    window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
    window.sessionStorage.removeItem(ADMIN_LOGIN_KEY);
    window.sessionStorage.setItem(ADMIN_AUTO_RESTORE_DISABLED_KEY, 'true');
  }

  getLastSignInMethod() {
    try {
      return window.localStorage.getItem(LAST_SIGN_IN_METHOD_KEY) || '';
    } catch {
      return '';
    }
  }

  async clearLocalSession() {
    window.localStorage.removeItem(LOCAL_SESSION_KEY);
    this.localSession = null;
  }

  async clearSession() {
    window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
    window.sessionStorage.removeItem(ADMIN_LOGIN_KEY);
    await this.clearLocalSession();
    window.sessionStorage.removeItem(MICROSOFT_RETURN_KEY);
    if (this.microsoft) {
      await this.microsoft.clearCache();
    }
  }

  async signOut() {
    const logoutUri = new URL(this.config.logoutPath, window.location.origin).toString();
    const provider = this.localSession && this.localSession.provider;
    const refreshToken = this.localSession && this.localSession.refreshToken;

    if (provider !== 'Microsoft' && refreshToken) {
      try {
        await this.cognito.send(new RevokeTokenCommand({
          ClientId: this.config.clientId,
          Token: refreshToken
        }));
      } catch {
        // Continue with local cleanup if revocation is unavailable.
      }
    }

    await this.clearSession();
    window.location.replace(logoutUri);
  }

  finishAuthentication(response, username = '') {
    if (response && response.AuthenticationResult) {
      this.storeAuthenticationResult(response.AuthenticationResult);
      return { signedIn: true };
    }

    if (response && response.ChallengeName === 'NEW_PASSWORD_REQUIRED' && response.Session) {
      return {
        signedIn: false,
        next: 'NEW_PASSWORD_REQUIRED',
        username: normalizeEmail(username),
        session: response.Session
      };
    }

    throw new Error('This account requires an authentication step that is not currently supported.');
  }

  storeAuthenticationResult(result, existingRefreshToken = '') {
    if (!result || !result.AccessToken || !result.IdToken) {
      throw new Error('Cognito did not return a complete user session.');
    }

    const profile = decodeJwtPayload(result.IdToken);
    const accessProfile = decodeJwtPayload(result.AccessToken);
    const session = {
      accessToken: result.AccessToken,
      idToken: result.IdToken,
      refreshToken: result.RefreshToken || existingRefreshToken,
      expiresAt: Number(accessProfile.exp) || Math.floor(Date.now() / 1000) + Number(result.ExpiresIn || 3600),
      email: normalizeEmail(profile.email),
      userId: String(profile.sub || ''),
      provider: 'cognito'
    };

    window.localStorage.setItem(LOCAL_SESSION_KEY, JSON.stringify(session));
    rememberLastSignInMethod('email');
    this.localSession = session;
  }

  storeMicrosoftSession(result) {
    const user = result && result.user;
    const session = {
      accessToken: String(result && result.sessionToken || ''),
      idToken: '',
      refreshToken: '',
      expiresAt: Number(result && result.expiresAt),
      email: normalizeEmail(user && user.userDetails),
      userId: String(user && user.userId || ''),
      provider: 'Microsoft'
    };

    if (!session.accessToken || !session.email || !session.userId || !Number.isFinite(session.expiresAt)) {
      throw new Error('The application did not return a complete Microsoft session.');
    }

    window.localStorage.setItem(LOCAL_SESSION_KEY, JSON.stringify(session));
    rememberLastSignInMethod('microsoft');
    this.localSession = session;
  }
}

function readLocalSession() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(LOCAL_SESSION_KEY));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function readAdministratorSession() {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(ADMIN_SESSION_KEY));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function readAdminLoginHint() {
  try {
    return normalizeEmail(window.localStorage.getItem(ADMIN_LOGIN_HINT_KEY));
  } catch {
    return '';
  }
}

function administratorSignInError(session, refreshFailure = '') {
  const base = session && session.message || 'Administrator sign-in could not be completed.';
  if (!session || session.diagnosticCode !== 'token-expired' || !session.diagnostic) return base;
  const detail = session.diagnostic;
  const parts = [base];
  if (detail.tokenIssuedAtUtc) parts.push(`Token issued: ${detail.tokenIssuedAtUtc}.`);
  if (detail.tokenExpiresAtUtc) parts.push(`Token expired: ${detail.tokenExpiresAtUtc}.`);
  if (detail.serverTimeUtc) parts.push(`Azure checked: ${detail.serverTimeUtc}.`);
  parts.push(`Browser checked: ${new Date().toISOString()}.`);
  if (Number.isFinite(detail.tokenLifetimeSeconds)) parts.push(`Token lifetime: ${formatSeconds(detail.tokenLifetimeSeconds)}.`);
  if (Number.isFinite(detail.expiredBySeconds)) parts.push(`Expired before Azure validation by: ${formatSeconds(detail.expiredBySeconds)}.`);
  if (refreshFailure) parts.push(`Forced token renewal result: ${refreshFailure}.`);
  return parts.join(' ');
}

function formatSeconds(value) {
  const total = Math.max(0, Math.floor(Number(value) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return [hours ? `${hours}h` : '', minutes ? `${minutes}m` : '', `${seconds}s`].filter(Boolean).join(' ');
}

function rememberLastSignInMethod(method) {
  try {
    window.localStorage.setItem(LAST_SIGN_IN_METHOD_KEY, method);
  } catch {
    // The sign-in session remains valid if this optional preference cannot be saved.
  }
}

function decodeJwtPayload(token) {
  try {
    const encoded = String(token || '').split('.')[1];
    const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return JSON.parse(decodeURIComponent(Array.from(atob(padded), (character) =>
      `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`
    ).join('')));
  } catch {
    return {};
  }
}

function getChallengeUsername(response, fallback) {
  const parameters = response && response.ChallengeParameters;
  const username = parameters && (parameters.USERNAME || parameters.USER_ID_FOR_SRP);
  return String(username || fallback || '').trim().toLowerCase();
}

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}
